import { useState } from "react";
import { Copy, Check, Hash, ExternalLink } from "lucide-react";
import { fmtAmount, fmtDate } from "@/lib/render";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Row({ label, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-xs uppercase tracking-wider text-muted-foreground shrink-0">{label}</span>
      <span data-testid={testid} className="text-sm text-foreground text-right break-all">{children}</span>
    </div>
  );
}

export const TransactionDetails = ({ txn, accounts, onClose }) => {
  const [copied, setCopied] = useState(false);
  const acctName = (id) => accounts.find((a) => a.id === id)?.name || (id === "external-clearing" ? "External Clearing" : id || "—");
  const copy = () => { navigator.clipboard.writeText(txn.tx_hash); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <Dialog open={!!txn} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border text-foreground max-w-lg" data-testid="transaction-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Transaction details</DialogTitle>
          <DialogDescription className="text-muted-foreground">Reference <span className="font-mono">{txn?.reference}</span></DialogDescription>
        </DialogHeader>
        {txn && (
          <>
            <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-indigo-500 mb-1.5">
                <Hash className="h-3.5 w-3.5" /> Transaction Hash (TXHS)
              </div>
              {txn.tx_hash ? (
                <div className="flex items-start gap-2">
                  <code data-testid="transaction-detail-txhash" className="font-mono text-xs break-all flex-1 text-foreground">{txn.tx_hash}</code>
                  <Button size="icon" variant="ghost" onClick={copy} data-testid="transaction-detail-copy-hash" className="h-7 w-7 shrink-0">
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              ) : null}
              {txn.tx_hash && txn.explorer_url && (
                <a href={txn.explorer_url} target="_blank" rel="noopener noreferrer" data-testid="transaction-detail-explorer-link"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-500 hover:underline">
                  View on block explorer <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {!txn.tx_hash && (
                <p data-testid="transaction-detail-txhash" className="text-xs text-muted-foreground">Not yet processed — hash is generated on completion.</p>
              )}
            </div>
            <div className="divide-y divide-border">
              <Row label="Type" testid="transaction-detail-type"><span className="capitalize">{txn.type}</span></Row>
              <Row label="Amount" testid="transaction-detail-amount"><span className="font-mono">{fmtAmount(txn.amount)} {txn.currency}</span></Row>
              <Row label={txn.type === "transfer" ? "From" : "Account"}>{acctName(txn.account_id)}</Row>
              {txn.counter_account_id && <Row label="To">{acctName(txn.counter_account_id)}</Row>}
              <Row label="Status" testid="transaction-detail-status"><span className="capitalize">{txn.status}</span></Row>
              <Row label="Description">{txn.description || "—"}</Row>
              {txn.source && <Row label="Source"><span className="font-mono text-xs">{txn.source}</span></Row>}
              <Row label="Transaction ID"><span className="font-mono text-xs">{txn.id}</span></Row>
              <Row label="Created">{fmtDate(txn.created_at)}</Row>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
